import { useCallback, useMemo } from "react";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import type { Role } from "@models/access";

/** The permission keys a role holds. */
export const roleKeys = (role: Pick<Role, "modules">): string[] =>
  role.modules.flatMap((m) => m.permissions.filter((p) => p.assigned).map((p) => p.key));

/**
 * The UI side of core's no-escalation rules (§5.2): an admin can only grant,
 * edit or delete what they hold themselves, and never change a role they hold.
 * Core enforces the same rules; this only disables what would be refused.
 */
export const useEscalationGuard = () => {
  const { effectivePermissions, hasPermission } = useAdminPermissions();
  const held = useMemo(() => new Set(effectivePermissions), [effectivePermissions]);

  /** Keys of `keys` the actor does not hold. */
  const missing = useCallback((keys: string[]) => keys.filter((k) => !held.has(k)), [held]);

  /** The role's keys are all held: it may be assigned, and deleted. */
  const canGrantRole = useCallback((role: Role) => missing(roleKeys(role)).length === 0, [missing]);

  /** A role editable by this actor: not held by them, not stronger than them. */
  const canEditRole = useCallback(
    (role: Role) => !role.heldByActor && canGrantRole(role),
    [canGrantRole],
  );

  return { held, hasPermission, missing, canGrantRole, canEditRole };
};
